// ————————————————————————————————————————————
// Frame Engine Foundation
//
// This is the engine responsible for playing frame-based diamond media
// (Nivoda `certificate.v360`). It is NOT a Viewer, NOT UI, NOT a modal,
// and renders nothing to the DOM itself. It owns frame math, frame
// resolution, frame caching, and playback state only.
//
// Why this exists / what it's built on:
//   - Media Slot Foundation (assets/diamond-card-media.js) already
//     normalizes a diamond's `v360` field into `{ url, frame_count }` via
//     resolveDiamondMedia() and detects the v360 capability. This engine
//     is the next layer down: given that same v360 descriptor, it is
//     what a future Viewer calls to actually step through frames.
//   - Real Nivoda V360 Asset Audit (research only, not committed as code)
//     confirmed: `v360.url` is a bare base path, NOT an interactive
//     viewer, video, or embeddable media. Individual frames are static
//     JPEGs at `${url}/${index}.jpg` for index 0..frame_count-1. Nivoda
//     ships zero playback behavior (no autoplay/drag/inertia/pause) —
//     100% of that is ours to build.
//   - Initial Orientation / top_index Audit (GraphQL introspection against
//     the live Nivoda staging API, not guessed): `V360Info.top_index` is
//     real, confirmed via `__type(name: "V360Info")`, and now requested by
//     ivanov-jewelry-api/src/services/diamond-search.ts. Sampled real
//     diamonds return non-zero values in both directions (e.g. "251",
//     "226", "134", "62" alongside plenty of "0"s) — frame 0 is correct
//     for some assets and wrong for others, so it is never a safe
//     default/guess, only a real per-asset value. The GraphQL field type
//     is String (not Int), so resolveFrameMedia() below parses numeric
//     strings, not just JS numbers.
//
// Architecture:
//
//   Media Slot  →  Frame Engine (this file)  →  Viewer (not started)
//
// The engine knows only: frame_count, top_index, current_frame, frame
// URLs, loading state, and cache state. It has zero knowledge of
// buttons, modals, prices, CTAs, typography, layout, mouse/touch
// listeners, or animation easing — those all belong to the future
// Viewer, which will consume this engine's plain-data/callback API.
//
// Internal responsibilities (each independent, each replaceable later
// without touching the public API):
//
//   1. Media resolution     — resolveFrameMedia()
//   2. Frame URL resolution — buildFrameUrl() / normalizeFrameIndex()
//   3. Frame cache          — createFrameCache()
//   4. Loading queue        — createLoadQueue()
//   5. Preload strategy     — defaultPreloadStrategy()
//   6. Drag → frame index   — consumeDragPixels() (2.5 px/frame accumulator)
//   7. Autoplay / idle      — play()/pause() + the engine's state machine
//
// Nothing here optimizes for performance yet (no Service Worker, no CDN
// proxy, no request batching/priority hints) — only the shape a future
// optimization would slot into.
// ————————————————————————————————————————————
(function (global) {
  "use strict";

  // Every state the engine itself can be in. UNINITIALIZED and LOADING
  // are transient (before/while `load()` resolves the first frame); IDLE
  // is the "loaded, static, not autoplaying" resting state a Viewer sees
  // most of the time; PLAYING is the autoplay state. ERROR is a fatal
  // load failure (no frame could ever be resolved); DESTROYED is
  // terminal.
  const STATES = {
    UNINITIALIZED: "uninitialized",
    LOADING: "loading",
    IDLE: "idle",
    PLAYING: "playing",
    ERROR: "error",
    DESTROYED: "destroyed",
  };

  // Per-frame lifecycle, tracked independently of the engine-level state
  // above. A single frame can be QUEUED/LOADING/LOADED/ERROR at any time
  // regardless of whether the engine overall is IDLE or PLAYING.
  const FRAME_STATES = {
    IDLE: "idle",
    QUEUED: "queued",
    LOADING: "loading",
    LOADED: "loaded",
    ERROR: "error",
  };

  function isFiniteNumber(value) {
    return typeof value === "number" && Number.isFinite(value);
  }

  // Nivoda's `V360Info.top_index` field is a GraphQL String (confirmed via
  // schema introspection - e.g. "251", "0", "134"), not an Int, even
  // though it always represents a frame index. Accepts a real JS number
  // too (defensive - covers a future backend change or a hand-built test
  // fixture) so every call site can hand this raw `top_index` verbatim
  // without normalizing it first. Returns null (not 0) when nothing usable
  // is present, so the caller can tell "no value provided" apart from "a
  // real index of 0" and only fall back deliberately.
  function parseFrameIndexValue(value) {
    if (isFiniteNumber(value)) return value;
    if (typeof value === "string" && value.trim().length > 0) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  // ————————————————————————————————————————————
  // 1) Media resolution
  //
  // Normalizes whatever v360-shaped object the caller hands in (today:
  // `{ url, frame_count }` from diamond-card-media.js's
  // resolveDiamondMedia(); tomorrow: the same object with a real
  // `top_index`/`renumbered`/`dl_link` once the backend query grows)
  // into the flat, engine-internal shape everything below relies on.
  // This is the only function that ever reads the raw v360 field names —
  // every other function in this file only ever sees { url, frameCount,
  // topIndex }.
  // ————————————————————————————————————————————
  // Prepared Turntable PoC (reversible): optional presentation frame list
  // from a local manifest. When present, buildFrameUrl resolves
  // frames[index].url instead of `${url}/${index}.jpg`. Absent on every
  // raw Nivoda path — zero behavior change unless a caller opts in.
  function resolvePreparedFrames(raw, frameCount) {
    const list =
      (raw && Array.isArray(raw.frames) && raw.frames) ||
      (raw &&
        raw.presentation &&
        Array.isArray(raw.presentation.frames) &&
        raw.presentation.frames) ||
      null;
    if (!list || list.length === 0) return null;
    if (frameCount > 0 && list.length !== frameCount) return null;
    const frames = new Array(list.length);
    for (let i = 0; i < list.length; i += 1) {
      const entry = list[i] || {};
      const url = typeof entry.url === "string" ? entry.url : null;
      if (!url) return null;
      frames[i] = {
        url: url,
        sourceIndex: isFiniteNumber(entry.sourceIndex)
          ? Math.floor(entry.sourceIndex)
          : null,
      };
    }
    return frames;
  }

  function resolveFrameMedia(raw) {
    raw = raw || {};
    const preparedFrames = resolvePreparedFrames(raw, 0);
    const frameCountFromPrepared = preparedFrames ? preparedFrames.length : 0;
    const frameCount =
      frameCountFromPrepared > 0
        ? frameCountFromPrepared
        : isFiniteNumber(raw.frame_count) && raw.frame_count > 0
          ? Math.floor(raw.frame_count)
          : 0;
    const url =
      typeof raw.url === "string" && raw.url.length > 0
        ? raw.url.replace(/\/+$/, "")
        : preparedFrames && preparedFrames[0] && preparedFrames[0].url
          ? preparedFrames[0].url
          : null;
    // Real per-asset value from diamond-search.ts today (see file header
    // audit note) - a numeric GraphQL String, e.g. "251". Only an absent/
    // unparseable value (diamond has no v360, or a future asset genuinely
    // omits it) falls back to 0 - frame 0 is a last-resort default here,
    // never an assumption that it is the correct orientation.
    // Prepared Turntable PoC: presentation sequences are rotated so
    // presentation index 0 is upright — prefer explicit top_index when
    // provided, otherwise 0 for prepared media.
    const parsedTopIndex = parseFrameIndexValue(raw.top_index);
    // Prepared sequences are rotated so presentation index 0 is upright;
    // missing top_index still falls back to 0 (same as raw Nivoda).
    const rawTopIndex = parsedTopIndex !== null ? Math.floor(parsedTopIndex) : 0;
    const topIndex =
      frameCount > 0 ? clamp(rawTopIndex, 0, frameCount - 1) : 0;
    const frames =
      preparedFrames && preparedFrames.length === frameCount
        ? preparedFrames
        : null;

    return {
      url: url,
      frameCount: frameCount,
      topIndex: topIndex,
      frames: frames,
      // Whether there's anything playable at all. Kept as a named flag
      // rather than making callers re-derive `url && frameCount > 0`
      // everywhere. Prepared media may omit a Nivoda base `url` and still
      // be playable via frames[].url.
      isPlayable:
        frameCount > 0 &&
        (Boolean(url) || Boolean(frames && frames.length === frameCount)),
    };
  }

  // ————————————————————————————————————————————
  // 2) Frame URL resolution
  // ————————————————————————————————————————————

  // Wraps an arbitrary integer into the valid [0, frameCount) range, so
  // next()/previous()/drag can move past either end and spin around —
  // exactly how a real 360 dial behaves. Returns 0 for an empty/unknown
  // sequence rather than throwing, so callers never need a frameCount>0
  // guard before calling this.
  function normalizeFrameIndex(index, frameCount) {
    if (!isFiniteNumber(frameCount) || frameCount <= 0) return 0;
    const wrapped = Math.trunc(index) % frameCount;
    return wrapped < 0 ? wrapped + frameCount : wrapped;
  }

  // The one place that knows Nivoda's frame naming convention
  // (`${base}/${index}.jpg`), confirmed via the V360 asset audit. If a
  // future asset audit reveals a different extension/pattern for some
  // media, this is the only line that needs to change.
  //
  // Prepared Turntable PoC (reversible): when `media.frames` is present
  // (from a local prepared manifest), resolve that presentation URL
  // instead of concatenating the raw Nivoda base path. Raw Nivoda path
  // unchanged when frames is absent.
  function buildFrameUrl(media, index) {
    if (!media || !media.isPlayable) return null;
    const safeIndex = normalizeFrameIndex(index, media.frameCount);
    if (media.frames && media.frames[safeIndex] && media.frames[safeIndex].url) {
      return media.frames[safeIndex].url;
    }
    if (!media.url) return null;
    return `${media.url}/${safeIndex}.jpg`;
  }

  // ————————————————————————————————————————————
  // 3) Frame cache
  //
  // An in-memory Map keyed by frame index. Each entry is
  // { state, url, image }: `url` alone is not enough to guarantee "no
  // refetch" — whether re-assigning the same URL to a fresh <img> hits
  // the browser's HTTP cache depends on Nivoda's cache-control/CORS
  // headers, which this engine does not control and has not verified.
  // Storing the already-decoded `Image` element itself (the exact object
  // fetchFrame() probed with) is the simplest foundation that guarantees
  // zero refetch regardless of server headers: a Viewer can paint that
  // element (or read its .src) directly instead of creating a new one.
  //
  // Still deliberately minimal — no persistence, no size limit, no
  // eviction ("do not optimize yet"). Resources are released the only
  // way plain remote-URL Images require: dropping references so the GC
  // can reclaim them, which clear() does. (No object URLs/blobs are
  // used here, so there is nothing to explicitly revoke.) Isolated
  // behind this factory so a future swap (e.g. a capped LRU, or a
  // persistent cache backed by the Cache API) only changes this
  // function, never the engine logic that calls it.
  // ————————————————————————————————————————————
  function createFrameCache() {
    const entries = new Map();

    function get(index) {
      return entries.get(index) || null;
    }

    function set(index, patch) {
      const prev = entries.get(index) || { state: FRAME_STATES.IDLE, url: null, image: null };
      const next = Object.assign({}, prev, patch);
      entries.set(index, next);
      return next;
    }

    function has(index) {
      const entry = entries.get(index);
      return Boolean(entry && entry.state === FRAME_STATES.LOADED);
    }

    function clear() {
      entries.clear();
    }

    // A frame-count-agnostic summary, so the engine's public
    // getSnapshot() can report cache state without the caller ever
    // touching this Map directly.
    function summary() {
      let loaded = 0;
      let loading = 0;
      let queued = 0;
      let error = 0;
      entries.forEach((entry) => {
        if (entry.state === FRAME_STATES.LOADED) loaded += 1;
        else if (entry.state === FRAME_STATES.LOADING) loading += 1;
        else if (entry.state === FRAME_STATES.QUEUED) queued += 1;
        else if (entry.state === FRAME_STATES.ERROR) error += 1;
      });
      return { loaded, loading, queued, error, tracked: entries.size };
    }

    return { get, set, has, clear, summary };
  }

  // ————————————————————————————————————————————
  // 4) Loading queue
  //
  // A bounded-concurrency FIFO queue (default 3 in-flight requests at
  // once, via `maxConcurrentLoads` — see restoration note below). Every
  // frame request funnels through here so the engine never fires more
  // than `maxConcurrentLoads` network requests at a time. `worker(index)`
  // does the actual fetch and must resolve/reject; this queue only owns
  // ordering and in-flight bookkeeping.
  //
  // Manual-drag smoothness restoration: this queue previously ran fully
  // sequentially (concurrency-1) — every priority frame a drag/swipe
  // requested had to wait for whatever single fetch was already
  // in-flight, even though the browser is happy to run several JPEG
  // probes in parallel. That serialization was the one piece of prior
  // drag-smoothing work an unrelated Undo All reverted; restoring only
  // this bounded concurrency bump (not the separate, since-abandoned
  // full progressive-background-sweep experiment) is what lets several
  // nearby/interaction frames resolve together instead of one-by-one
  // while dragging, with no change to autoplay's own fps/tick logic.
  //
  // Post-review changes:
  //   - Priority entries are inserted at the FRONT (`unshift`), not
  //     pushed to the back — the original `remove()` + `enqueue()`
  //     pairing used by requestFrame() looked like a promotion but
  //     actually re-appended to the tail, so an interaction-requested
  //     frame could get stuck behind background preload work. Fixed by
  //     giving enqueue() an explicit `{ priority }` option.
  //   - `maxSize` bounds `pending` so rapid navigation/drag can never
  //     grow the queue without bound. When full, the oldest *non-
  //     priority* (pure preload) entry is evicted first so interaction
  //     requests are never starved by background work; if every entry
  //     is priority, the oldest priority entry (back of the array,
  //     since priority entries are unshifted newest-first) is evicted
  //     instead of the newest.
  //   - `destroy()` flips an internal flag checked by processNext(), so
  //     a worker callback that resolves after destroy (an in-flight
  //     Image request cannot truly be cancelled) can no longer start
  //     further queued work.
  //
  // Rolling Forward Buffer experiment — three-tier priority (post-
  // review, smallest change over the prior two-tier priority/plain
  // split): every entry now carries a numeric `tier` instead of a
  // boolean `priority`, but the two tiers that already existed keep
  // their exact prior behavior byte-for-byte —
  //   TIER_INTERACTION (0) — `{ priority: true }` callers (manual drag's
  //     exact frame + its one-frame look-ahead, the engine's own
  //     immediate next autoplay frame): still unshifted to the front, so
  //     multiple same-tick interaction requests still resolve LIFO
  //     (most recent wins the front slot), exactly as before.
  //   TIER_BACKGROUND (2) — the old implicit "not priority" entries
  //     (preloadAround()'s small radius, the retired progressive sweep
  //     utility): still pushed to the tail, still plain FIFO.
  // The only new tier is in between:
  //   TIER_ROLLING (1) — `{ rolling: true }`, used exclusively by the
  //     rolling forward autoplay buffer (see maintainRollingBuffer()
  //     below) to keep replenishing ready-ahead frames without ever
  //     outranking a fresh manual/autoplay-immediate request, and
  //     without being starved by (or starving) generic background
  //     preload. Because TIER_INTERACTION always unshifts to position 0
  //     and TIER_BACKGROUND always pushes to the end, `pending` is
  //     structurally always sorted ascending by tier — inserting a new
  //     TIER_ROLLING entry just before the first tier-2 entry (or at the
  //     end if none exists yet) drops it exactly between the two
  //     existing groups, FIFO within its own group, with no change to
  //     either pre-existing group's insertion code path.
  // ————————————————————————————————————————————
  const TIER_INTERACTION = 0;
  const TIER_ROLLING = 1;
  const TIER_BACKGROUND = 2;

  function createLoadQueue(worker, options) {
    options = options || {};
    const maxSize = isFiniteNumber(options.maxSize) ? Math.max(1, Math.floor(options.maxSize)) : 16;
    const maxConcurrentLoads = isFiniteNumber(options.maxConcurrentLoads)
      ? Math.max(1, Math.floor(options.maxConcurrentLoads))
      : 3;
    const pending = []; // [{ index, tier }], sorted ascending by tier, processed from index 0
    const active = new Set(); // indices with an in-flight worker() call
    let destroyed = false;

    function indexOf(index) {
      for (let i = 0; i < pending.length; i += 1) {
        if (pending[i].index === index) return i;
      }
      return -1;
    }

    function isQueued(index) {
      return indexOf(index) !== -1;
    }

    // Inserts a fresh entry at the correct position for its tier without
    // disturbing any existing group's relative order (see the tier
    // comment above): tier 0 unshifts (LIFO, unchanged prior behavior),
    // tier 2 pushes (FIFO, unchanged prior behavior), tier 1 splices in
    // right before the first tier-2 entry (FIFO within tier 1, always
    // between the other two groups).
    function insertByTier(index, tier) {
      if (tier <= TIER_INTERACTION) {
        pending.unshift({ index, tier: TIER_INTERACTION });
      } else if (tier >= TIER_BACKGROUND) {
        pending.push({ index, tier: TIER_BACKGROUND });
      } else {
        let at = pending.findIndex((item) => item.tier > TIER_ROLLING);
        if (at === -1) at = pending.length;
        pending.splice(at, 0, { index, tier: TIER_ROLLING });
      }
    }

    function trimToCapacity() {
      while (pending.length > maxSize) {
        // Evict the single least-urgent entry present: an oldest
        // TIER_BACKGROUND entry first, else an oldest TIER_ROLLING
        // entry, else (everything left is TIER_INTERACTION) the oldest
        // interaction entry — which, because that tier is LIFO
        // (unshifted newest-first), sits at the back of the array, not
        // the front.
        let dropAt = pending.findIndex((item) => item.tier === TIER_BACKGROUND);
        if (dropAt === -1) dropAt = pending.findIndex((item) => item.tier === TIER_ROLLING);
        if (dropAt === -1) dropAt = pending.length - 1;
        pending.splice(dropAt, 1);
      }
    }

    // Fills every free concurrency slot (up to maxConcurrentLoads) with
    // the next pending item, so priority frames requested mid-drag no
    // longer queue up strictly one-at-a-time behind whatever was already
    // in flight.
    function processNext() {
      if (destroyed) return;
      while (active.size < maxConcurrentLoads && pending.length > 0) {
        const item = pending.shift();
        active.add(item.index);
        Promise.resolve()
          .then(() => worker(item.index))
          .catch(() => {
            /* worker is responsible for recording failure in the cache;
               the queue only needs to keep moving. */
          })
          .then(() => {
            active.delete(item.index);
            if (!destroyed) processNext();
          });
      }
    }

    // `index` cannot appear twice in `pending`/`active` (checked via
    // indexOf/active.has above) — this is the single choke point
    // everything else in the engine goes through, so duplicate queuing
    // is structurally impossible, not just unlikely.
    function enqueue(index, enqueueOptions) {
      if (destroyed || active.has(index)) return false;
      const priority = Boolean(enqueueOptions && enqueueOptions.priority);
      const rolling = Boolean(enqueueOptions && enqueueOptions.rolling);
      const tier = priority ? TIER_INTERACTION : (rolling ? TIER_ROLLING : TIER_BACKGROUND);
      const at = indexOf(index);
      if (at !== -1) {
        // Promote in place only if the new request is strictly more
        // urgent than whatever tier this index is already queued at —
        // e.g. a rolling-buffer frame that a fresh drag/autoplay tick
        // now needs immediately jumps to TIER_INTERACTION, but a
        // rolling-buffer re-request for a frame already at
        // TIER_INTERACTION must never demote it back down.
        if (tier < pending[at].tier) {
          pending.splice(at, 1);
          insertByTier(index, tier);
        }
        return false;
      }
      insertByTier(index, tier);
      trimToCapacity();
      processNext();
      return true;
    }

    function remove(index) {
      const at = indexOf(index);
      if (at !== -1) pending.splice(at, 1);
    }

    function clear() {
      pending.length = 0;
    }

    // Distinct from clear(): also prevents any in-flight worker
    // callback from starting further work once it resolves.
    function destroy() {
      destroyed = true;
      pending.length = 0;
    }

    return {
      enqueue,
      remove,
      clear,
      destroy,
      isQueued,
      get size() {
        return pending.length + active.size;
      },
    };
  }

  // ————————————————————————————————————————————
  // 5) Preload strategy
  //
  // Named and swappable so a future prefetch strategy (e.g. direction-
  // aware, based on drag velocity) is a config value, not a rewrite.
  // Today's default just radiates outward from the requested index by a
  // small, fixed radius — enough to make next()/previous() and short
  // drags feel instant without ever eagerly loading a whole sequence.
  // ————————————————————————————————————————————
  function defaultPreloadStrategy(index, frameCount, radius) {
    const indices = [];
    for (let step = 1; step <= radius; step += 1) {
      indices.push(normalizeFrameIndex(index + step, frameCount));
      indices.push(normalizeFrameIndex(index - step, frameCount));
    }
    // De-dupe (small frame counts can wrap into repeats) while keeping
    // stable order.
    return indices.filter((value, at) => indices.indexOf(value) === at);
  }

  // ————————————————————————————————————————————
  // 5b) Progressive/interleaved background preload plan
  //
  // Nivoda-Style Progressive V360 Loading: a real Nivoda Admin V360 asset
  // audit (DevTools Network panel, not speculation) showed frame requests
  // arriving out of linear order — coarse, widely-spaced indices first,
  // then batches that fill in between them — rather than 0,1,2,3....
  // This is the generic principle behind that observation (the exact
  // observed numbers are Nivoda's, not reproduced here): given only
  // `frameCount`, produce an ordering of every index 0..frameCount-1 that
  // visits a few widely-distributed positions first, then repeatedly
  // bisects the *largest remaining gaps* between already-planned
  // positions, until the whole rotation is covered.
  //
  // This is pure index math — it has no idea what "loaded" means, does
  // not touch the network/cache, and never re-visits an index once
  // planned. It only decides the *order* a future full-coverage sweep
  // should attempt indices in; whether/when each one actually gets
  // requested is entirely up to the caller (see feedProgressiveBackground
  // below), which is what keeps this safe to compute for any frameCount
  // (32/64/128/199/256/...) without ever loading anything eagerly itself.
  //
  // Algorithm (circular largest-gap bisection):
  //   - Start with a single "placed" point at `startIndex` (typically
  //     top_index — the frame already loaded first by load()). On a
  //     circle of frameCount positions, one placed point has exactly one
  //     gap: the entire rest of the circle.
  //   - Each pass takes every gap between two currently-placed points
  //     that has room (more than one unplaced index between them) and
  //     inserts its midpoint. Because every pass processes *all* current
  //     gaps at once, and they all start equal (one gap, the whole
  //     circle) and split roughly in half every pass, this naturally
  //     produces PASS 1 (sparse, evenly-distributed coverage) -> PASS 2
  //     (fills the largest/only gaps from pass 1) -> PASS 3+ (keeps
  //     subdividing) without any special-casing per pass number.
  //   - A gap of length 1 (its two endpoints are already adjacent
  //     indices) has no midpoint to add and is left alone — those
  //     remaining single-frame gaps are exactly the "final coverage"
  //     tail of the plan.
  //   - Every index is inserted at most once (tracked via `placed`), so
  //     the result is a permutation of [0, frameCount) with zero
  //     duplicates, computed purely from frameCount + startIndex.
  // ————————————————————————————————————————————
  function buildProgressiveFramePlan(frameCount, startIndex) {
    if (!isFiniteNumber(frameCount) || frameCount <= 0) return [];
    const count = Math.floor(frameCount);
    const start = normalizeFrameIndex(startIndex, count);

    const order = [start];
    if (count <= 1) return order;

    const placed = new Set([start]);
    const sorted = [start]; // kept ascending so consecutive entries are circular neighbors

    function insertSorted(index) {
      let lo = 0;
      let hi = sorted.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (sorted[mid] < index) lo = mid + 1;
        else hi = mid;
      }
      sorted.splice(lo, 0, index);
    }

    // One "pass" = bisect every gap that currently has room, using a
    // snapshot of `sorted` taken before this pass's own insertions (so
    // two gaps discovered in the same pass never see each other's new
    // midpoint — that would just be inserting into an already-narrower
    // gap ahead of schedule, i.e. skipping straight past a coarser pass).
    while (order.length < count) {
      const gaps = [];
      for (let i = 0; i < sorted.length; i += 1) {
        const a = sorted[i];
        const b = sorted[(i + 1) % sorted.length];
        // A single placed point has no real neighbor yet — the gap wraps
        // all the way around the circle back to itself (length = count).
        const dist = sorted.length === 1 ? count : (b - a + count) % count;
        if (dist > 1) gaps.push({ a: a, dist: dist });
      }
      if (gaps.length === 0) break; // every index already placed

      gaps.forEach((gap) => {
        const mid = (gap.a + Math.floor(gap.dist / 2)) % count;
        if (placed.has(mid)) return; // defensive; bisection math guarantees this never fires
        placed.add(mid);
        insertSorted(mid);
        order.push(mid);
      });
    }

    return order;
  }

  // ————————————————————————————————————————————
  // 6) Drag position → frame index
  //
  // Incremental fractional accumulator. The Viewer still hands this
  // engine a pixel delta (typically the cumulative delta since
  // pointerdown); the engine converts that into incremental movement,
  // adds it to a remainder, and emits a frame step only when a full
  // pixels-per-frame unit has accumulated. Math.round on the total
  // drag distance is what produced the measured slow-drag dead zones
  // (hold for several pixels, then jump). Math.trunc keeps left/right
  // symmetric and leaves the unused fraction for the next update.
  // ————————————————————————————————————————————
  const DEFAULT_DRAG_PIXELS_PER_FRAME = 2.5;

  function consumeDragPixels(accumulator, pixelsPerFrame) {
    const safeSensitivity =
      isFiniteNumber(pixelsPerFrame) && pixelsPerFrame > 0
        ? pixelsPerFrame
        : DEFAULT_DRAG_PIXELS_PER_FRAME;
    const steps = Math.trunc(accumulator / safeSensitivity);
    return {
      steps,
      remainder: accumulator - steps * safeSensitivity,
    };
  }

  // Kept as a pure helper for callers/tests that only need the whole
  // frame-step count from a pixel amount. Uses the same trunc rule as
  // the live accumulator — not the old total-delta Math.round mapping.
  function frameDeltaFromDragDelta(deltaPixels, pixelsPerFrame) {
    return consumeDragPixels(deltaPixels, pixelsPerFrame).steps;
  }

  // ————————————————————————————————————————————
  // Tiny internal event emitter. This is the entire "notify the Viewer"
  // surface — no DOM events, no globals beyond the instance itself. A
  // future Viewer subscribes to whatever it needs (`framechange`,
  // `statechange`, `error`, ...) instead of polling the engine.
  // ————————————————————————————————————————————
  function createEmitter() {
    const listeners = new Map();

    function on(event, handler) {
      if (!listeners.has(event)) listeners.set(event, new Set());
      listeners.get(event).add(handler);
      return () => off(event, handler);
    }

    function off(event, handler) {
      const set = listeners.get(event);
      if (set) set.delete(handler);
    }

    function emit(event, payload) {
      const set = listeners.get(event);
      if (!set) return;
      // Copy before iterating so a handler unsubscribing mid-emit is safe.
      Array.from(set).forEach((handler) => handler(payload));
    }

    function clear() {
      listeners.clear();
    }

    return { on, off, emit, clear };
  }

  // ————————————————————————————————————————————
  // The engine
  //
  // One instance per media descriptor (i.e. per diamond a future Viewer
  // is currently showing). Nothing here is shared/global mutable state,
  // so multiple engines can exist independently (e.g. warm-loading the
  // next diamond in a list) without interfering with each other.
  // ————————————————————————————————————————————
  function createFrameEngine(config) {
    config = config || {};

    const emitter = createEmitter();
    const cache = createFrameCache();
    const preloadRadius = isFiniteNumber(config.preloadRadius)
      ? Math.max(0, Math.floor(config.preloadRadius))
      : 2;
    const dragSensitivity = isFiniteNumber(config.dragSensitivity)
      ? Math.max(1, config.dragSensitivity)
      : DEFAULT_DRAG_PIXELS_PER_FRAME; // pixels of drag per one frame step
    const autoplayFps = isFiniteNumber(config.fps) ? Math.max(1, config.fps) : 12;
    const step = isFiniteNumber(config.step) ? Math.max(1, Math.floor(config.step)) : 1;
    const maxQueueSize = isFiniteNumber(config.maxQueueSize)
      ? Math.max(1, Math.floor(config.maxQueueSize))
      : 16;
    // Manual-drag smoothness restoration (see createLoadQueue's comment):
    // defaults to 6 concurrent frame fetches rather than 1, so a Viewer
    // never has to opt in to get smooth dragging. (Raised from 3 to 6
    // after real cold-throughput benchmarking on the ES-AA-08813 V360
    // asset — measured 256-frame HTTP/1.1 S3 delivery — showed 6
    // materially clears the 15 FPS autoplay supply deficit and reduces
    // p95 latency, with no measured benefit from going higher.)
    const maxConcurrentLoads = isFiniteNumber(config.maxConcurrentLoads)
      ? Math.max(1, Math.floor(config.maxConcurrentLoads))
      : 6;
    // V360 Rolling Forward Buffer experiment (measured-root-cause audit:
    // startup ready-ahead was effectively 2 frames ≈ 133ms against a
    // measured ~190-200ms real per-frame latency — the tiny existing
    // preloadAround() neighborhood postpones that starvation but does
    // not solve it during a sustained cold rotation). How many
    // sequential frames ahead of currentFrame, in the active autoplay
    // direction, this engine tries to keep LOADED/in-flight at all
    // times while PLAYING (see maintainRollingBuffer()). Zero (the
    // default) disables the feature entirely, so every existing caller/
    // test that never opts in keeps today's exact behavior — a future
    // Viewer opts in explicitly with an experiment value (e.g. 15) via
    // `rollingBufferTarget`, kept distinct from `preloadRadius` above,
    // which stays a small bidirectional neighborhood and is unrelated
    // to autoplay direction.
    const rollingBufferTarget = isFiniteNumber(config.rollingBufferTarget)
      ? Math.max(0, Math.floor(config.rollingBufferTarget))
      : 0;

    let media = resolveFrameMedia(config.media);
    let state = STATES.UNINITIALIZED;
    let currentFrame = media.topIndex;
    let autoplayTimer = null;
    let dragStartFrame = null;
    let dragAccumulator = 0;
    let dragLastDeltaPixels = 0;
    let dragLastRawFrame = null;
    let destroyed = false;
    // Direction the rolling buffer should prepare frames toward even
    // while not actively PLAYING (e.g. right after load(), or during
    // the idle window after a manual release) — set from the most
    // recent play() call, defaulting to forward. Never touched by
    // drag, which has its own direction-aware look-ahead.
    let lastAutoplayDirection = 1;
    // Indices this engine has requested purely to fill the rolling
    // buffer (TIER_ROLLING), so a fresh interaction can yield/clear
    // whichever of them are still merely QUEUED (not yet LOADING/
    // LOADED) instead of leaving them to compete for a concurrency slot
    // ahead of newly-irrelevant background work. See
    // yieldRollingBuffer().
    const rollingRequestedIndices = new Set();

    const loadQueue = createLoadQueue(fetchFrame, { maxSize: maxQueueSize, maxConcurrentLoads });

    function setState(next) {
      if (state === next) return;
      state = next;
      emitter.emit("statechange", { state });
    }

    // The only function that actually touches the network. Uses an
    // `Image` purely as a fetch-and-decode probe (never inserted into
    // the DOM) so the engine can detect success/404-failure — this is
    // not rendering, it's how a frame gets warmed into the browser's own
    // HTTP cache before a Viewer ever asks to paint it.
    function fetchFrame(index) {
      const url = buildFrameUrl(media, index);
      if (!url) {
        cache.set(index, { state: FRAME_STATES.ERROR, url: null });
        return Promise.resolve();
      }

      cache.set(index, { state: FRAME_STATES.LOADING, url });
      emitter.emit("framestatechange", { index, state: FRAME_STATES.LOADING });

      return new Promise((resolve) => {
        const probe = new Image();
        // A real in-flight Image request cannot be cancelled — this
        // guard is what makes destroy-during-load safe: if destroy()
        // already ran by the time this fires, the result is discarded
        // instead of repopulating a cleared cache or emitting an event
        // no one (emitter.clear() already ran too) can hear anyway.
        probe.onload = () => {
          if (destroyed) { resolve(); return; }
          // Cache the decoded probe itself (see createFrameCache notes)
          // so a Viewer's <img> can reuse this exact resource with no
          // dependency on CDN cache-control headers.
          cache.set(index, { state: FRAME_STATES.LOADED, url, image: probe });
          emitter.emit("framestatechange", { index, state: FRAME_STATES.LOADED, url });
          resolve();
        };
        // 404 fallback: a failed frame is recorded as an error, never
        // thrown/unhandled. getDisplayFrameUrl() below is what decides
        // what to show in its place — this handler's only job is
        // bookkeeping.
        probe.onerror = () => {
          if (destroyed) { resolve(); return; }
          cache.set(index, { state: FRAME_STATES.ERROR, url });
          emitter.emit("framestatechange", { index, state: FRAME_STATES.ERROR });
          resolve();
        };
        probe.src = url;
      });
    }

    // Ensures a frame is at least queued, without blocking the caller.
    // Idempotent: safe to call repeatedly for the same index — the
    // queue itself (createLoadQueue) is what structurally guarantees no
    // duplicate/lost work, this function just decides priority.
    //
    // Retry contract (post-review): a frame that has reached ERROR is a
    // *permanent* failure for the lifetime of this engine instance —
    // requestFrame() is called on every getDisplayFrameUrl() poll,
    // every autoplay tick, and every drag update, so without this guard
    // a single missing frame would be re-requested (and re-404) forever
    // as the user drags/plays across it. `allowRetry` is the one
    // explicit escape hatch, set only by retryFrame() below; nothing
    // else may resurrect an ERROR entry.
    // `rolling` (Rolling Forward Buffer experiment): marks this request
    // as TIER_ROLLING in the load queue — more urgent than plain
    // background preload, but never able to outrank (or demote) an
    // interaction/`priority: true` request for the same index. Only
    // maintainRollingBuffer() below ever passes this.
    function requestFrame(index, options) {
      if (destroyed) return;
      if (!media.isPlayable) return;
      const priority = Boolean(options && options.priority);
      const rolling = Boolean(options && options.rolling);
      const allowRetry = Boolean(options && options.allowRetry);
      const entry = cache.get(index);
      if (entry && (entry.state === FRAME_STATES.LOADED || entry.state === FRAME_STATES.LOADING)) {
        return;
      }
      if (entry && entry.state === FRAME_STATES.ERROR && !allowRetry) {
        return; // permanently failed — no automatic retry loop
      }
      if (!entry || entry.state === FRAME_STATES.IDLE || entry.state === FRAME_STATES.ERROR) {
        cache.set(index, { state: FRAME_STATES.QUEUED, url: buildFrameUrl(media, index) });
        emitter.emit("framestatechange", { index, state: FRAME_STATES.QUEUED });
      }
      loadQueue.enqueue(index, { priority, rolling });
    }

    // Explicit recovery path for a future Viewer (e.g. a manual "retry"
    // affordance, or a connectivity-restored heuristic) — the only way
    // an ERROR frame can be re-requested short of load()'s full reset.
    // No-op (not an error) if the frame was never attempted or isn't
    // currently in ERROR, so a Viewer can call this defensively without
    // checking state first.
    function retryFrame(index) {
      if (destroyed) return;
      if (!media.isPlayable) return;
      const safeIndex = normalizeFrameIndex(index, media.frameCount);
      const entry = cache.get(safeIndex);
      if (!entry || entry.state !== FRAME_STATES.ERROR) return;
      requestFrame(safeIndex, { priority: true, allowRetry: true });
    }

    function preloadAround(index) {
      const strategy = typeof config.preloadStrategy === "function"
        ? config.preloadStrategy
        : defaultPreloadStrategy;
      strategy(index, media.frameCount, preloadRadius).forEach((frameIndex) => {
        requestFrame(frameIndex);
      });
    }

    // ————————————————————————————————————————————
    // Rolling Forward Buffer (V360 Rolling Forward Buffer experiment)
    //
    // Root cause this targets: preloadAround()'s tiny bidirectional
    // radius (2 frames) is enough to survive a couple of autoplay ticks
    // but not a sustained cold rotation — real measured per-frame
    // latency (~190-200ms) comfortably outruns a 2-frame reserve at 15
    // FPS (~133ms). This does not replace preloadAround() (still runs
    // every goToFrame() for general bidirectional neighbor coverage) —
    // it adds a deeper, direction-aware, TIER_ROLLING reserve that only
    // exists while frames are actually being *consumed* forward
    // (PLAYING, or the idle window immediately after one), and is
    // capped at a small bounded window (`rollingBufferTarget`), never a
    // full-sequence sweep.
    //
    // Deliberately NOT "keep the longest sequential run of LOADED
    // frames at N": a single ERROR frame inside the window must not
    // make this stop requesting everything past it (existing ERROR
    // semantics stay authoritative — see requestFrame()'s permanent-
    // failure guard, which this simply inherits by routing through it).
    // Instead this just ensures every index in the fixed-size forward
    // window from `currentFrame` is at least requested (or already
    // resolved/permanently failed) — cheap to call every tick, since
    // requestFrame() is already a no-op for anything LOADED/LOADING/
    // QUEUED/ERROR.
    function maintainRollingBuffer(direction) {
      if (destroyed || !media.isPlayable) return;
      if (rollingBufferTarget <= 0) return;
      const windowSize = Math.min(rollingBufferTarget, Math.max(0, media.frameCount - 1));
      const dir = direction === -1 ? -1 : 1;
      for (let step = 1; step <= windowSize; step += 1) {
        const index = normalizeFrameIndex(currentFrame + dir * step, media.frameCount);
        requestFrame(index, { rolling: true });
        rollingRequestedIndices.add(index);
      }
    }

    // Manual interaction / stop-replenishing contract: called the
    // instant a drag begins (and on every pause()) so a rolling-buffer
    // request that is still merely QUEUED — i.e. has not yet consumed a
    // concurrency slot — is pulled back out of the queue rather than
    // continuing to occupy queue position against now-more-relevant
    // work. An entry that has already progressed to LOADING cannot be
    // cancelled (same real-network constraint as everywhere else in
    // this file) and is simply left to resolve into the cache normally.
    function yieldRollingBuffer() {
      if (rollingRequestedIndices.size === 0) return;
      rollingRequestedIndices.forEach((index) => {
        const entry = cache.get(index);
        if (entry && entry.state === FRAME_STATES.QUEUED) {
          loadQueue.remove(index);
          cache.set(index, { state: FRAME_STATES.IDLE, url: null });
        }
      });
      rollingRequestedIndices.clear();
    }

    // Instrumentation-friendly (not a logging call) read of exactly the
    // metric the measured-root-cause audit tracked: how many
    // consecutive frames starting immediately after `currentFrame`, in
    // `direction`, are right now LOADED. Stops counting at the first
    // gap (still-loading/queued/idle) or ERROR frame, matching the
    // audit's own definition of "ready-ahead" — a Viewer/test can call
    // this at any time without the engine needing to keep its own
    // rolling min/avg history.
    function getSequentialReadyAhead(direction) {
      if (!media.isPlayable) return 0;
      const dir = direction === -1 ? -1 : 1;
      let count = 0;
      let index = currentFrame;
      const limit = Math.max(0, media.frameCount - 1);
      for (let step = 0; step < limit; step += 1) {
        index = normalizeFrameIndex(index + dir, media.frameCount);
        const entry = cache.get(index);
        if (entry && entry.state === FRAME_STATES.LOADED) {
          count += 1;
        } else {
          break;
        }
      }
      return count;
    }

    // ————————————————————————————————————————————
    // Frame lookup / 404 fallback
    //
    // getDisplayFrameUrl() is the one method a Viewer actually needs for
    // painting. Its contract (post-review):
    //
    //   { requestedIndex, displayedIndex, url, image, isFallback, state }
    //
    // `requestedIndex` is always the (normalized) index the caller asked
    // for. `displayedIndex` is what is *actually* being returned to
    // paint, which can legitimately differ from requestedIndex — but the
    // engine never hides that fact, so a Viewer can decide for itself
    // whether to show a "loading"/dimmed affordance when
    // displayedIndex !== requestedIndex, instead of silently rendering a
    // neighboring frame under the wrong label.
    //
    // Fallback order, chosen specifically to prevent visual jumping
    // during drag/autoplay (finding: the previous version re-searched
    // outward from the requested index on every call, which could hop
    // between different stale neighbors frame-to-frame instead of
    // holding still):
    //
    //   1. Requested frame is LOADED           -> show it, becomes the
    //                                              new "last displayed".
    //   2. A frame has been displayed before   -> HOLD that exact frame
    //                                              (same index/url) while
    //                                              the requested one loads.
    //   3. Nothing has ever been displayed yet -> bootstrap only: search
    //                                              outward for any loaded
    //                                              neighbor, then top_index.
    //   4. Nothing loaded anywhere yet         -> url/image are null,
    //                                              state reports why.
    //
    // Retry contract: this method is polled on every drag/autoplay
    // tick, so its internal requestFrame() call must never resurrect a
    // permanently-failed frame — requestFrame() itself now no-ops for
    // ERROR entries (see its comment) unless retryFrame(index) was
    // called explicitly. `state` on the returned result still reports
    // ERROR honestly; it just won't keep re-requesting the network.
    // ————————————————————————————————————————————
    let lastDisplayed = null; // { index, url, image } | null

    function displayResult(requestedIndex, displayedIndex, entry, isFallback) {
      if (displayedIndex !== null) {
        lastDisplayed = { index: displayedIndex, url: entry.url, image: entry.image || null };
      }
      return {
        requestedIndex,
        displayedIndex,
        url: displayedIndex === null ? null : entry.url,
        image: displayedIndex === null ? null : entry.image || null,
        isFallback,
        state: displayedIndex === null ? (entry ? entry.state : FRAME_STATES.IDLE) : FRAME_STATES.LOADED,
      };
    }

    function getDisplayFrameUrl(index) {
      if (!media.isPlayable) {
        return {
          requestedIndex: null,
          displayedIndex: null,
          url: null,
          image: null,
          isFallback: false,
          state: FRAME_STATES.ERROR,
        };
      }

      const safeIndex = normalizeFrameIndex(index, media.frameCount);
      const entry = cache.get(safeIndex);

      if (entry && entry.state === FRAME_STATES.LOADED) {
        return displayResult(safeIndex, safeIndex, entry, false);
      }

      requestFrame(safeIndex, { priority: true });
      const requestedEntry = cache.get(safeIndex); // re-read: requestFrame() above may have just queued it

      // Hold the last frame this engine actually reported as shown,
      // rather than jumping to a different already-loaded neighbor —
      // this is what prevents the requested frame from being silently
      // represented as some *other* frame's identity mid-drag/autoplay.
      if (lastDisplayed) {
        return {
          requestedIndex: safeIndex,
          displayedIndex: lastDisplayed.index,
          url: lastDisplayed.url,
          image: lastDisplayed.image,
          isFallback: true,
          state: requestedEntry ? requestedEntry.state : FRAME_STATES.IDLE,
        };
      }

      // Bootstrap only (nothing has ever been displayed by this engine
      // instance yet, e.g. a Viewer polling before load() resolves):
      // search outward for any already-loaded neighbor, then top_index.
      for (let radius = 1; radius < media.frameCount; radius += 1) {
        const forward = normalizeFrameIndex(safeIndex + radius, media.frameCount);
        const backward = normalizeFrameIndex(safeIndex - radius, media.frameCount);
        const forwardEntry = cache.get(forward);
        if (forwardEntry && forwardEntry.state === FRAME_STATES.LOADED) {
          return displayResult(safeIndex, forward, forwardEntry, true);
        }
        const backwardEntry = cache.get(backward);
        if (backwardEntry && backwardEntry.state === FRAME_STATES.LOADED) {
          return displayResult(safeIndex, backward, backwardEntry, true);
        }
      }

      const topEntry = cache.get(media.topIndex);
      if (topEntry && topEntry.state === FRAME_STATES.LOADED) {
        return displayResult(safeIndex, media.topIndex, topEntry, true);
      }

      return {
        requestedIndex: safeIndex,
        displayedIndex: null,
        url: null,
        image: null,
        isFallback: false,
        state: requestedEntry ? requestedEntry.state : FRAME_STATES.IDLE,
      };
    }

    function goToFrame(index, options) {
      options = options || {};
      if (!media.isPlayable) return;
      const nextFrame = normalizeFrameIndex(index, media.frameCount);
      const changed = nextFrame !== currentFrame;
      currentFrame = nextFrame;
      requestFrame(currentFrame, { priority: true });
      if (!options.silent) preloadAround(currentFrame);
      if (changed || options.force) {
        emitter.emit("framechange", { index: currentFrame });
      }
    }

    // ————————————————————————————————————————————
    // Public API
    // ————————————————————————————————————————————

    function load(nextMedia) {
      if (destroyed) return Promise.resolve();
      // A media swap must not inherit fractional movement or an
      // in-progress drag from the previous diamond.
      dragStartFrame = null;
      resetDragMotionState();
      if (nextMedia) media = resolveFrameMedia(nextMedia);

      if (!media.isPlayable) {
        setState(STATES.ERROR);
        emitter.emit("error", { reason: "no-playable-media" });
        return Promise.resolve();
      }

      cache.clear();
      loadQueue.clear();
      lastDisplayed = null;
      currentFrame = media.topIndex;
      setState(STATES.LOADING);

      return fetchFrame(currentFrame).then(() => {
        if (destroyed) return;
        const entry = cache.get(currentFrame);
        if (!entry || entry.state !== FRAME_STATES.LOADED) {
          setState(STATES.ERROR);
          emitter.emit("error", { reason: "top-frame-failed", index: currentFrame });
          return;
        }
        setState(STATES.IDLE);
        emitter.emit("framechange", { index: currentFrame });
        preloadAround(currentFrame);
        // Startup behavior (Rolling Forward Buffer experiment): forward
        // frames begin preparing the instant top_index is visible, not
        // only once/if a Viewer later calls play() — a Viewer gating
        // autoplay start on "N sequential forward frames LOADED" would
        // otherwise wait on frames nothing had ever requested yet.
        maintainRollingBuffer(lastAutoplayDirection);
      });
    }

    function setFrame(index) {
      if (state === STATES.UNINITIALIZED || state === STATES.ERROR || destroyed) return;
      goToFrame(index);
    }

    function next() {
      if (destroyed) return;
      goToFrame(currentFrame + step);
    }

    function previous() {
      if (destroyed) return;
      goToFrame(currentFrame - step);
    }

    function play(options) {
      if (destroyed || !media.isPlayable) return;
      if (autoplayTimer) return; // already playing
      const fps = (options && isFiniteNumber(options.fps)) ? Math.max(1, options.fps) : autoplayFps;
      const direction = (options && options.direction === -1) ? -1 : 1;
      lastAutoplayDirection = direction;
      setState(STATES.PLAYING);
      emitter.emit("play", { fps, direction });
      // Seed the rolling buffer immediately on play(), not only on the
      // first tick — matches maintainRollingBuffer()'s own idempotent
      // no-flood guarantee (bounded to rollingBufferTarget), just run
      // one tick earlier than the interval below would otherwise.
      maintainRollingBuffer(direction);
      autoplayTimer = global.setInterval(() => {
        goToFrame(currentFrame + step * direction);
        // Rolling buffer behavior: every tick consumes one frame off
        // the front of the ready-ahead window, so the tail is topped up
        // by exactly the same amount here — this is what keeps the
        // reserve at ~rollingBufferTarget frames indefinitely during a
        // sustained cold rotation instead of only surviving the first
        // couple of ticks.
        maintainRollingBuffer(direction);
      }, Math.round(1000 / fps));
    }

    function pause() {
      if (!autoplayTimer) return;
      global.clearInterval(autoplayTimer);
      autoplayTimer = null;
      if (state === STATES.PLAYING) setState(STATES.IDLE);
      emitter.emit("pause", { index: currentFrame });
      // Manual-interaction contract: rolling replenishment stops the
      // instant autoplay stops (the interval above is already cleared,
      // so no *new* rolling requests can be issued) and any of its
      // requests that never made it past QUEUED are yielded immediately
      // rather than left to linger in the queue.
      yieldRollingBuffer();
    }

    function resetDragMotionState() {
      dragAccumulator = 0;
      dragLastDeltaPixels = 0;
      dragLastRawFrame = null;
    }

    // Drag lifecycle: beginDrag() marks the drag active and zeros the
    // fractional accumulator; updateDrag() is called with the Viewer's
    // *cumulative* pixel delta since pointerdown, which this engine
    // converts into incremental movement so only whole 2.5 px units become
    // frame steps; endDrag() clears the anchor and remainder.
    // The Viewer supplies raw pixel deltas from its own pointer
    // listeners — this engine never touches PointerEvent/MouseEvent.
    function beginDrag() {
      if (destroyed || !media.isPlayable) return;
      pause();
      // pause() only yields rolling-buffer requests when autoplay's own
      // interval was actually running — but the rolling buffer can also
      // be mid-fill from load()'s own startup seed (or endDrag()'s
      // post-release seed) before play() was ever called, and a fresh
      // drag must yield it in that case too.
      yieldRollingBuffer();
      dragStartFrame = currentFrame;
      resetDragMotionState();
    }

    // Manual Drag Stall Audit finding: updateDrag() deliberately calls
    // goToFrame(..., { silent: true }) below, skipping preloadAround(),
    // so the exact frame under the cursor is the *only* one requested —
    // on purpose, so a drag never triggers the same radius-2 burst a
    // settled setFrame()/next() does. Measured effect (real engine, real
    // timing, see audit): whether a given frame ever actually paints
    // during a drag is a straight race between how long the drag dwells
    // on that index before moving to the next one and that one frame's
    // own network round trip — with zero head start, any index the drag
    // passes through faster than the network can answer is guaranteed
    // to never paint (its eventual LOADED result lands for a frame that
    // is no longer current, and is discarded), which is what a shopper
    // sees as a stall. Requesting exactly one extra frame — the very
    // next index in the direction the drag is already moving, still
    // priority but enqueued *before* the current frame so the current
    // frame's own request always stays frontmost (see requestFrame()
    // call order below) — gives that next frame a head start equal to
    // however long the drag dwells on the current one, which is exactly
    // the quantity the race depends on. This is the smallest change
    // that targets the demonstrated cause: still one frame at a time,
    // still bounded by the existing maxConcurrentLoads cap, no change
    // to autoplay's own scheduler/fps or to preloadRadius.
    function updateDrag(deltaPixels) {
      if (destroyed || dragStartFrame === null) return;
      const incoming = isFiniteNumber(deltaPixels) ? deltaPixels : 0;
      const increment = incoming - dragLastDeltaPixels;
      dragLastDeltaPixels = incoming;
      if (increment === 0) return;

      dragAccumulator += increment;
      const consumed = consumeDragPixels(dragAccumulator, dragSensitivity);
      dragAccumulator = consumed.remainder;
      const steps = consumed.steps;
      if (steps === 0) return;

      // Positive pointer delta (drag right) still decreases the frame
      // index — same direction contract as the previous total-delta path.
      const targetFrame = currentFrame - steps;
      if (dragLastRawFrame !== null) {
        const direction = steps > 0 ? -1 : 1;
        requestFrame(normalizeFrameIndex(targetFrame + direction, media.frameCount), { priority: true });
      }
      dragLastRawFrame = targetFrame;
      goToFrame(targetFrame, { silent: true });
    }

    function endDrag() {
      if (dragStartFrame === null) return;
      dragStartFrame = null;
      resetDragMotionState();
      preloadAround(currentFrame);
      // After-manual-release behavior: start preparing forward frames
      // relative to the exact frame the drag ended on (no snap back to
      // top_index) during the existing idle-before-resume window, so
      // that whenever a Viewer's own idle timer later calls play()
      // again, the "wait for N sequential forward frames" gate has a
      // head start instead of starting from zero. Uses whatever
      // direction the last play() call used (default forward) — endDrag
      // has no directional signal of its own to improve on.
      maintainRollingBuffer(lastAutoplayDirection);
    }

    function preload(indices) {
      if (destroyed || !media.isPlayable) return;
      (indices || []).forEach((index) => requestFrame(index));
    }

    function getState() {
      return state;
    }

    function getCurrentFrame() {
      return currentFrame;
    }

    function getFrameCount() {
      return media.frameCount;
    }

    function getTopIndex() {
      return media.topIndex;
    }

    function getFrameUrl(index) {
      return buildFrameUrl(media, index);
    }

    function getFrameState(index) {
      const entry = cache.get(normalizeFrameIndex(index, media.frameCount));
      return entry ? entry.state : FRAME_STATES.IDLE;
    }

    // Everything a Viewer needs to know in one call, matching exactly
    // the data surface this engine is scoped to: frame_count, top_index,
    // current_frame, loading state, and cache state. No URLs are
    // embedded here on purpose — call getDisplayFrameUrl()/getFrameUrl()
    // for those, since "what to paint right now" can change between one
    // snapshot and the next frame tick.
    function getSnapshot() {
      return {
        state,
        frameCount: media.frameCount,
        topIndex: media.topIndex,
        currentFrame,
        loading: state === STATES.LOADING || loadQueue.size > 0,
        cache: cache.summary(),
      };
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      dragStartFrame = null;
      resetDragMotionState();
      pause(); // cancels the autoplay setInterval timer (and yields rolling-buffer requests, if any were pending)
      rollingRequestedIndices.clear(); // pause() only yields when autoplay was actually running; this covers the IDLE-but-rolling-seeded case too
      loadQueue.destroy(); // stops queued work; in-flight worker becomes a no-op via the `destroyed` checks above
      cache.clear();
      lastDisplayed = null;
      setState(STATES.DESTROYED);
      emitter.clear(); // no further event can reach a listener after this point
    }

    return {
      // Lifecycle
      load,
      destroy,
      // Navigation
      setFrame,
      next,
      previous,
      // Autoplay
      play,
      pause,
      // Drag
      beginDrag,
      updateDrag,
      endDrag,
      // Prefetch
      preload,
      // Recovery
      retryFrame,
      // Lookup
      getFrameUrl,
      getDisplayFrameUrl,
      getFrameState,
      getCurrentFrame,
      getFrameCount,
      getTopIndex,
      getState,
      getSnapshot,
      // Instrumentation (Rolling Forward Buffer experiment) — read-only,
      // no logging side effect; a Viewer/test harness polls this itself
      // rather than the engine emitting noisy production telemetry.
      getSequentialReadyAhead,
      // Events
      on: emitter.on,
      off: emitter.off,
    };
  }

  global.DiamondFrameEngine = {
    STATES,
    FRAME_STATES,
    resolveFrameMedia,
    buildFrameUrl,
    normalizeFrameIndex,
    DEFAULT_DRAG_PIXELS_PER_FRAME,
    consumeDragPixels,
    frameDeltaFromDragDelta,
    // Exported as a pure index-math utility only (see its own comment) —
    // it is not wired into any automatic background sweep here, since
    // that engine-integration was a separate, already-abandoned
    // experiment this restoration deliberately does not bring back.
    buildProgressiveFramePlan,
    createFrameEngine,
  };
})(window);
